import { useTranslation } from "@/shared/hooks/useTranslation";
import { Button } from "@/shared/ui/button";
import { usePwaInstall } from "@/features/pwa/hooks/use-pwa-install";

/**
 * Bottom-anchored install banner for the PWA. Renders ONLY when the app is
 * installable AND not already installed AND the user hasn't dismissed the
 * prompt this session.
 *
 * Why a banner (not a Dialog):
 *   - Less intrusive on mobile, where install prompts feel like ads when
 *     they block the UI.
 *   - Native Android already shows a mini-infobar; ours sits at the bottom
 *     and disappears on scroll/dismiss.
 *   - A Dialog (Radix) would need a focus trap; for a 2-button banner, the
 *     native tab order is more than enough.
 */
export const PwaInstallPrompt = (): React.JSX.Element | null => {
  const { isInstallable, isInstalled, declined, promptInstall, dismissInstall } = usePwaInstall();
  const { t } = useTranslation();

  if (!isInstallable || isInstalled || declined) {
    return null;
  }

  const handleInstall = async () => {
    await promptInstall();
  };

  return (
    <div
      role="region"
      aria-label={t("pwa.install.title")}
      data-testid="pwa-install-prompt"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-700/60 bg-slate-900/95 px-4 py-3 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-slate-900/80 sm:bottom-4 sm:left-auto sm:right-4 sm:max-w-sm sm:rounded-lg sm:border"
    >
      <div className="flex flex-col gap-2">
        <p className="text-sm font-semibold text-slate-100">{t("pwa.install.title")}</p>
        <p className="text-xs text-slate-300">{t("pwa.install.description")}</p>
        <div className="mt-1 flex items-center gap-2">
          <Button size="sm" variant="primary" onClick={handleInstall} data-testid="pwa-install-button">
            {t("pwa.install.button")}
          </Button>
          <Button size="sm" variant="tertiary" onClick={dismissInstall} data-testid="pwa-install-dismiss">
            {t("pwa.install.dismiss")}
          </Button>
        </div>
      </div>
    </div>
  );
};
